/**
 * The two ways to put a feasibility report on the loan, shared by every
 * affordance in the Feasibility section (the header buttons and the Report
 * panel's empty state fire the SAME flow — hence a hook plus one mounted menu
 * and one mounted file input, rather than a self-contained button pair).
 *
 * "On file" is a DOC-TYPE test over the loan's documents (see feasibilityModel),
 * so the two ways to satisfy it are:
 *
 *   • ASSIGN  — the report is already on the loan under the wrong doc type
 *               ("Uncategorized", "Property Documents", …). Re-typing it via
 *               `POST /files/update_loan_doc_metadata/{loanId}` is all that is
 *               needed, and it is what the underwriter would otherwise do from
 *               the Documents grid.
 *   • UPLOAD  — a plain `POST /files/upload_loan_docs/{loanId}` with the
 *               docGroup / docType already stamped on the metadata entry, so
 *               the file lands classified instead of going through the queue's
 *               classify step. Each entry needs a stable `id`: the backend
 *               correlates the binary part to its metadata BY part filename ===
 *               metadata id.
 *
 * Plus the step AFTER either one: EXTRACT. Neither assigning nor uploading
 * parses anything — extraction is an explicit
 * `POST /files/parse_extract_docs/{loanId}` whose extractor is chosen by
 * docType (`extractor_router.py:110` maps "Project Feasibility Analysis" and
 * "General Contractor Review Form (GCR)" to the feasibility extractor). Without
 * it a re-typed document sits at "On file ✓ / Extracted ✗" forever, so the
 * section offers the run rather than dead-ending.
 */
import { Box, InputBase, Menu, MenuItem } from "@mui/material";
import PropTypes from "prop-types";
import { useCallback, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { v4 as uuidv4 } from "uuid";

import Flx from "../../../../components/layout/Flx";
import Txt from "../../../../components/typography/Txt";
import { isDocHidden } from "../../../../_src_shared/utils/functions/isDocHidden";
import { getExpirationDaysForDocType } from "../../../../constants/documents/docTypeExpirationDurations";
import { useLoanFilesHook } from "../../../../hooks/useLoanFilesHook";
import { loanDrilldownSet } from "../../../../redux/actions/loanDrilldownActions";
import { T } from "./budgetDrawsTheme";

/**
 * Where an assigned / uploaded feasibility lands. Both values are VERBATIM from
 * the loan-file catalog (`fileDocGroupAndTypesFallback` → "Third Party
 * Reports"); "Project Feasibility Analysis" is the catalog member of
 * FEASIBILITY_DOCTYPES that budget review reads, and the one the required-docs
 * checklist asks for by name.
 */
export const FEASIBILITY_DOC_GROUP = "Third Party Reports";
export const FEASIBILITY_DOC_TYPE = "Project Feasibility Analysis";

export const docName = (d) => d?.file_display_name || d?.name || "Untitled";

export const useFeasibilityDocActions = ({ dealId, docs }) => {
  const dispatch = useDispatch();
  const loanDocuments = useSelector((s) => s.loanDrilldown?.loanDocuments);
  const { updateLoanDocMetadata, uploadLoanDocs, parseAndExtractDocuments } =
    useLoanFilesHook();

  const [anchor, setAnchor] = useState(null);
  const [filter, setFilter] = useState("");
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  // Everything visible on the loan that is NOT already counted as a feasibility
  // report — those are the files that could be mis-typed.
  const onFileIds = useMemo(
    () => new Set((docs || []).map((d) => d?._id)),
    [docs],
  );
  const candidates = useMemo(() => {
    const term = filter.trim().toLowerCase();
    return (loanDocuments || [])
      .filter((d) => d && !isDocHidden(d) && !onFileIds.has(d._id))
      .filter((d) =>
        term
          ? `${docName(d)} ${d.docType || ""} ${d.docGroup || ""}`
              .toLowerCase()
              .includes(term)
          : true,
      )
      .sort((a, b) => docName(a).localeCompare(docName(b)));
  }, [loanDocuments, onFileIds, filter]);

  const closeMenu = useCallback(() => {
    setAnchor(null);
    setFilter("");
  }, []);

  const assign = useCallback(
    (doc) => {
      if (!doc?._id || !dealId) return;
      closeMenu();
      setBusy(true);
      updateLoanDocMetadata({
        loanId: dealId,
        data: [
          {
            _id: doc._id,
            docGroup: FEASIBILITY_DOC_GROUP,
            docType: FEASIBILITY_DOC_TYPE,
          },
        ],
        onSuccessFn: (d) => {
          dispatch(loanDrilldownSet(d));
          setBusy(false);
        },
        onFailFn: () => setBusy(false),
      });
    },
    [dealId, closeMenu, updateLoanDocMetadata, dispatch],
  );

  const onInputChange = useCallback(
    (e) => {
      const picked = Array.from(e.target.files || []);
      e.target.value = ""; // allow re-picking the same file
      if (!picked.length || !dealId) return;
      const days = getExpirationDaysForDocType(FEASIBILITY_DOC_TYPE);
      setBusy(true);
      uploadLoanDocs({
        loanId: dealId,
        files: picked.map((file) => ({
          id: uuidv4(), // the backend matches the binary part to this id
          file,
          name: file.name,
          file_display_name: file.name,
          docGroup: FEASIBILITY_DOC_GROUP,
          docType: FEASIBILITY_DOC_TYPE,
          reportGuidelineExpirationDays: days ?? null,
        })),
        onSuccessMsg:
          picked.length > 1
            ? `${picked.length} files uploaded`
            : "Feasibility report uploaded",
        onSuccessFn: (d) => {
          dispatch(loanDrilldownSet(d));
          setBusy(false);
        },
        onFailFn: () => setBusy(false),
      });
    },
    [dealId, uploadLoanDocs, dispatch],
  );

  /**
   * Send the feasibility documents already on the loan for parsing/extraction.
   * The job is queued on the service bus, so success here means "accepted" —
   * the review appears on the next load of the section, not immediately.
   */
  const runExtraction = useCallback(() => {
    if (!dealId || !docs?.length) return;
    setBusy(true);
    parseAndExtractDocuments({
      dealId,
      files: docs,
      onSuccessFn: () => setBusy(false),
      onFailFn: () => setBusy(false),
    });
  }, [dealId, docs, parseAndExtractDocuments]);

  return {
    busy,
    canExtract: Boolean(dealId) && (docs?.length || 0) > 0,
    runExtraction,
    openAssign: (e) => setAnchor(e.currentTarget),
    startUpload: () => inputRef.current?.click(),
    /** Spread onto <FeasibilityAssignMenu />. */
    menuProps: {
      anchor,
      candidates,
      filter,
      onFilterChange: setFilter,
      onPick: assign,
      onClose: closeMenu,
    },
    /** Spread onto the single hidden <input type="file" />. */
    inputProps: { ref: inputRef, type: "file", multiple: true, hidden: true, onChange: onInputChange },
  };
};

/** The "assign an existing loan document" picker. Mounted once per section. */
export const FeasibilityAssignMenu = ({
  anchor,
  candidates,
  filter,
  onFilterChange,
  onPick,
  onClose,
}) => (
  <Menu
    anchorEl={anchor}
    open={Boolean(anchor)}
    onClose={onClose}
    slotProps={{ paper: { sx: { width: 400, maxHeight: 380 } } }}
  >
    <Box sx={{ px: 1.25, pt: 0.5, pb: 1 }}>
      <Txt sx={{ display: "block", fontSize: 11.5, color: T.muted, mb: 0.75 }}>
        Set a document&apos;s type to &ldquo;{FEASIBILITY_DOC_TYPE}&rdquo;
      </Txt>
      {/* A plain input so the Menu's own typeahead doesn't swallow keystrokes. */}
      <InputBase
        autoFocus
        fullWidth
        value={filter}
        onChange={(e) => onFilterChange(e.target.value)}
        onKeyDown={(e) => e.stopPropagation()}
        placeholder="Search loan documents"
        sx={{
          px: 1,
          py: 0.375,
          fontSize: 12.5,
          border: `1px solid ${T.line}`,
          borderRadius: "6px",
        }}
      />
    </Box>
    {candidates.length === 0 ? (
      <Box sx={{ px: 1.5, py: 1.25 }}>
        <Txt sx={{ fontSize: 12, color: T.faint }}>
          {filter ? "No matching documents" : "No other documents on file"}
        </Txt>
      </Box>
    ) : (
      candidates.map((doc) => (
        <MenuItem key={doc._id} onClick={() => onPick(doc)} sx={{ py: 0.75 }}>
          <Flx column sx={{ minWidth: 0 }}>
            <Box
              sx={{
                fontSize: 12.5,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {docName(doc)}
            </Box>
            <Box sx={{ fontSize: 11, color: T.muted }}>
              {doc.docType || "Uncategorized"}
              {doc.docGroup ? ` · ${doc.docGroup}` : ""}
            </Box>
          </Flx>
        </MenuItem>
      ))
    )}
  </Menu>
);

FeasibilityAssignMenu.propTypes = {
  anchor: PropTypes.any,
  candidates: PropTypes.array,
  filter: PropTypes.string,
  onFilterChange: PropTypes.func,
  onPick: PropTypes.func,
  onClose: PropTypes.func,
};
